import React, {createContext, useContext, useState, useCallback} from 'react'
import {client} from '../../../../../utils/api-client'
import {
  USE_MOCK,
  mockCheckStatus,
  mockSendOtp,
  mockVerifyOtp,
} from '../proposal-auth-mock-data'

const STORAGE_KEY = 'proposal_auth_token'

const ProposalAuthContext = createContext(null)

export function ProposalAuthProvider({children, initialToken}) {
  const [authToken, setAuthTokenRaw] = useState(
    () => initialToken || sessionStorage.getItem(STORAGE_KEY) || null,
  )
  const [isVerified, setIsVerified] = useState(false)
  const [maskedContact, setMaskedContact] = useState(null)
  const [channel, setChannel] = useState('email')
  const [mfaRequired, setMfaRequired] = useState(null)
  const [proposalExists, setProposalExists] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [otpSent, setOtpSent] = useState(false)

  const setAuthToken = useCallback(token => {
    if (token) {
      sessionStorage.setItem(STORAGE_KEY, token)
    } else {
      sessionStorage.removeItem(STORAGE_KEY)
    }
    setAuthTokenRaw(token)
  }, [])

  const resetAuth = useCallback(() => {
    sessionStorage.removeItem(STORAGE_KEY)
    setAuthTokenRaw(null)
    setIsVerified(false)
    setOtpSent(false)
    setError('Your session has expired. Please verify your identity again.')
  }, [])

  const checkStatus = useCallback(async randomId => {
    setIsLoading(true)
    setError(null)
    try {
      const result = USE_MOCK
        ? await mockCheckStatus(randomId)
        : await client(`proposal-auth/status/${randomId}`)
      setProposalExists(result.exists)
      if (result.exists) {
        setMaskedContact(result.maskedContact || result.maskedEmail)
        setChannel(result.channel || 'email')
        if (result.token) {
          setAuthToken(result.token)
        }
        setMfaRequired(result.mfaRequired ?? false)
      } else {
        setMfaRequired(false)
      }
      return result
    } catch (err) {
      setError(err.message || 'Unable to load proposal')
      setProposalExists(false)
      setMfaRequired(false)
      return {exists: false}
    } finally {
      setIsLoading(false)
    }
  }, [])

  const sendOtp = useCallback(async randomId => {
    setIsLoading(true)
    setError(null)
    try {
      const result = USE_MOCK
        ? await mockSendOtp(randomId)
        : await client('proposal-auth/send-otp', {data: {proposalId: randomId}})
      setMaskedContact(result.maskedContact || result.maskedEmail)
      setChannel(result.channel || 'email')
      setOtpSent(true)
      return result
    } catch (err) {
      setError(err.message || 'Failed to send verification code')
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [])

  const verifyOtp = useCallback(async (randomId, otp) => {
    setIsLoading(true)
    setError(null)
    try {
      const result = USE_MOCK
        ? await mockVerifyOtp(randomId, otp)
        : await client('proposal-auth/verify-otp', {
            data: {proposalId: randomId, otp},
          })
      setAuthToken(result.token)
      setIsVerified(true)
      return result
    } catch (err) {
      setError(err.message || 'Verification failed')
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [])

  const value = {
    authToken,
    isVerified,
    mfaRequired,
    proposalExists,
    maskedContact,
    channel,
    isLoading,
    error,
    otpSent,
    checkStatus,
    sendOtp,
    verifyOtp,
    setError,
    resetAuth,
  }

  return (
    <ProposalAuthContext.Provider value={value}>
      {children}
    </ProposalAuthContext.Provider>
  )
}

export function useProposalAuth() {
  const context = useContext(ProposalAuthContext)
  if (!context) {
    throw new Error('useProposalAuth must be used within ProposalAuthProvider')
  }
  return context
}

export function useProposalAuthClient() {
  const {authToken, resetAuth} = useProposalAuth()

  const proposalClient = useCallback(
    async (endpoint, options = {}) => {
      try {
        return await client(endpoint, {
          ...options,
          token: authToken,
          headers: {
            'app-type': 'client-proposal-link',
            ...options.headers,
          },
        })
      } catch (err) {
        if (err.status === 401) {
          resetAuth()
        }
        throw err
      }
    },
    [authToken, resetAuth],
  )

  return proposalClient
}
